<script setup>
// Envia uma localização do WhatsApp pela conversa aberta.
// O atendente cola o link do Maps (ou coordenadas) e pode salvar na conta.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MessageApi from 'dashboard/api/inbox/message';
import SavedLocationsAPI from 'dashboard/api/savedLocations';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  conversationId: { type: Number, required: true },
});

const emit = defineEmits(['sent']);

const { t } = useI18n();
const dialogRef = ref(null);
const sending = ref(false);
const saving = ref(false);
const parsing = ref(false);
const formError = ref('');
const mapsInput = ref('');
const name = ref('');
const address = ref('');
const latitude = ref(null);
const longitude = ref(null);
const mapUrl = ref('');
const saveForLater = ref(false);
const savedLocations = ref([]);
const parseTimer = ref(null);
const pendingRemoveId = ref(null);

const hasCoords = computed(
  () => latitude.value != null && longitude.value != null
);

const canSend = computed(
  () => hasCoords.value && !sending.value && !parsing.value
);

const canSaveChecked = computed(() => {
  if (!saveForLater.value) return true;
  return Boolean(name.value.trim());
});

const parsedSummary = computed(() => {
  if (!hasCoords.value) return '';
  const coords = `${latitude.value}, ${longitude.value}`;
  return name.value.trim() ? `${name.value.trim()} (${coords})` : coords;
});

const reset = () => {
  mapsInput.value = '';
  name.value = '';
  address.value = '';
  latitude.value = null;
  longitude.value = null;
  mapUrl.value = '';
  saveForLater.value = false;
  formError.value = '';
  sending.value = false;
  saving.value = false;
  parsing.value = false;
  pendingRemoveId.value = null;
};

const loadSaved = async () => {
  try {
    const { data } = await SavedLocationsAPI.get();
    savedLocations.value = Array.isArray(data) ? data : [];
  } catch {
    savedLocations.value = [];
  }
};

const open = async () => {
  reset();
  await loadSaved();
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const applyParsed = data => {
  latitude.value = data.latitude;
  longitude.value = data.longitude;
  mapUrl.value = data.map_url || data.mapUrl || '';
  if (data.title && !name.value.trim()) {
    name.value = data.title;
  }
  if (data.address && !address.value.trim()) {
    address.value = data.address;
  }
  formError.value = '';
};

const clearParsed = () => {
  latitude.value = null;
  longitude.value = null;
  mapUrl.value = '';
};

const parseErrorMessage = errorCode => {
  switch (errorCode) {
    case 'short_link_resolve_failed':
      return t('CONVERSATION.LOCATION.ERRORS.SHORT_LINK_RESOLVE_FAILED');
    case 'short_link_blocked_host':
      return t('CONVERSATION.LOCATION.ERRORS.SHORT_LINK_BLOCKED_HOST');
    case 'invalid_coordinates':
      return t('CONVERSATION.LOCATION.ERRORS.INVALID_COORDINATES');
    case 'blank_input':
      return t('CONVERSATION.LOCATION.ERRORS.BLANK_INPUT');
    case 'name_blank':
      return t('CONVERSATION.LOCATION.ERRORS.NAME_BLANK');
    case 'limit_reached':
      return t('CONVERSATION.LOCATION.ERRORS.LIMIT_REACHED');
    case 'duplicate':
      return t('CONVERSATION.LOCATION.ERRORS.DUPLICATE');
    case 'save_failed':
      return t('CONVERSATION.LOCATION.ERRORS.SAVE_FAILED');
    default:
      return t('CONVERSATION.LOCATION.ERRORS.UNRECOGNIZED_MAPS_LINK');
  }
};

const parseInput = async () => {
  const value = mapsInput.value.trim();
  if (!value) {
    clearParsed();
    return;
  }
  parsing.value = true;
  formError.value = '';
  try {
    const { data } = await SavedLocationsAPI.parse(value);
    applyParsed(data);
  } catch (error) {
    clearParsed();
    formError.value = parseErrorMessage(error?.response?.data?.error);
  } finally {
    parsing.value = false;
  }
};

watch(mapsInput, () => {
  if (parseTimer.value) clearTimeout(parseTimer.value);
  parseTimer.value = setTimeout(parseInput, 350);
});

const selectSaved = item => {
  latitude.value = Number(item.latitude);
  longitude.value = Number(item.longitude);
  name.value = item.name || '';
  address.value = item.address || '';
  mapUrl.value =
    item.map_url ||
    `https://maps.google.com/?q=${item.latitude},${item.longitude}`;
  mapsInput.value = mapUrl.value;
  formError.value = '';
};

const requestRemoveSaved = item => {
  pendingRemoveId.value = item.id;
};

const cancelRemoveSaved = () => {
  pendingRemoveId.value = null;
};

const confirmRemoveSaved = async item => {
  pendingRemoveId.value = null;
  try {
    await SavedLocationsAPI.delete(item.id);
    savedLocations.value = savedLocations.value.filter(
      entry => entry.id !== item.id
    );
  } catch {
    useAlert(t('CONVERSATION.LOCATION.REMOVE_FAILED'));
  }
};

const maybeSave = async () => {
  if (!saveForLater.value) return;
  if (!name.value.trim()) {
    formError.value = t('CONVERSATION.LOCATION.NAME_REQUIRED');
    throw new Error('name_required');
  }
  saving.value = true;
  try {
    const { data } = await SavedLocationsAPI.create({
      name: name.value.trim(),
      latitude: latitude.value,
      longitude: longitude.value,
      address: address.value.trim() || undefined,
    });
    savedLocations.value = [...savedLocations.value, data];
  } catch (error) {
    const code = error?.response?.data?.error;
    if (code === 'duplicate') {
      // Already saved: keep going with the send.
      return;
    }
    formError.value = parseErrorMessage(code || 'save_failed');
    throw error;
  } finally {
    saving.value = false;
  }
};

const submit = async () => {
  if (!canSend.value || !canSaveChecked.value) {
    if (saveForLater.value && !name.value.trim()) {
      formError.value = t('CONVERSATION.LOCATION.NAME_REQUIRED');
    }
    return;
  }
  sending.value = true;
  formError.value = '';
  try {
    await maybeSave();
    const { data } = await MessageApi.sendLocation(props.conversationId, {
      latitude: latitude.value,
      longitude: longitude.value,
      title: name.value.trim() || undefined,
      address: address.value.trim() || undefined,
      map_url: mapUrl.value || undefined,
    });
    emit('sent', data);
    useAlert(t('CONVERSATION.LOCATION.SENT'));
    close();
  } catch (error) {
    if (error?.message === 'name_required') return;
    if (!formError.value) {
      formError.value =
        error?.response?.data?.error === 'waha_failed'
          ? t('CONVERSATION.LOCATION.FAILED')
          : t('CONVERSATION.LOCATION.FAILED');
    }
  } finally {
    sending.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="md"
    :title="t('CONVERSATION.LOCATION.TITLE')"
    :description="t('CONVERSATION.LOCATION.DESCRIPTION')"
    :confirm-button-label="t('CONVERSATION.LOCATION.SEND')"
    :cancel-button-label="t('CONVERSATION.LOCATION.CANCEL')"
    :disable-confirm-button="!canSend || !canSaveChecked"
    :is-loading="sending || saving"
    @confirm="submit"
  >
    <div class="flex flex-col gap-3">
      <Input
        v-model="mapsInput"
        :label="t('CONVERSATION.LOCATION.LINK')"
        :placeholder="t('CONVERSATION.LOCATION.LINK_PLACEHOLDER')"
        autofocus
      />
      <p
        v-if="hasCoords"
        class="mb-0 text-sm text-n-slate-11"
        data-testid="location-parsed-summary"
      >
        {{ t('CONVERSATION.LOCATION.PARSED', { summary: parsedSummary }) }}
      </p>
      <p v-else-if="parsing" class="mb-0 text-sm text-n-slate-10">
        {{ t('CONVERSATION.LOCATION.PARSING') }}
      </p>

      <Input
        v-model="name"
        :label="t('CONVERSATION.LOCATION.NAME')"
        :placeholder="t('CONVERSATION.LOCATION.NAME_PLACEHOLDER')"
      />
      <Input
        v-model="address"
        :label="t('CONVERSATION.LOCATION.ADDRESS')"
        :placeholder="t('CONVERSATION.LOCATION.ADDRESS_PLACEHOLDER')"
      />

      <label
        class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
      >
        <input
          v-model="saveForLater"
          type="checkbox"
          class="size-4 rounded border-n-weak"
        />
        <span>{{ t('CONVERSATION.LOCATION.SAVE_FOR_LATER') }}</span>
      </label>

      <div v-if="savedLocations.length" class="flex flex-col gap-2">
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('CONVERSATION.LOCATION.SAVED_TITLE') }}
        </p>
        <ul class="m-0 flex list-none flex-col gap-1 p-0">
          <li
            v-for="item in savedLocations"
            :key="item.id"
            class="flex flex-col gap-1 rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1.5"
          >
            <div class="flex items-center gap-2">
              <button
                type="button"
                class="min-w-0 flex-1 truncate text-left text-sm text-n-slate-12 hover:text-n-brand"
                @click="selectSaved(item)"
              >
                <span class="font-medium">{{ item.name }}</span>
                <span class="text-n-slate-10">
                  {{
                    t('CONVERSATION.LOCATION.SAVED_COORDS', {
                      latitude: item.latitude,
                      longitude: item.longitude,
                    })
                  }}
                </span>
              </button>
              <NextButton
                v-if="pendingRemoveId !== item.id"
                type="button"
                icon="i-ri-delete-bin-line"
                slate
                faded
                xs
                :aria-label="t('CONVERSATION.LOCATION.REMOVE')"
                @click="requestRemoveSaved(item)"
              />
            </div>
            <div
              v-if="pendingRemoveId === item.id"
              class="flex items-center justify-between gap-2 text-xs text-n-slate-11"
            >
              <span>
                {{
                  t('CONVERSATION.LOCATION.REMOVE_CONFIRM', { name: item.name })
                }}
              </span>
              <div class="flex gap-1">
                <NextButton
                  type="button"
                  slate
                  faded
                  xs
                  :label="t('CONVERSATION.LOCATION.CANCEL')"
                  @click="cancelRemoveSaved"
                />
                <NextButton
                  type="button"
                  ruby
                  solid
                  xs
                  :label="t('CONVERSATION.LOCATION.REMOVE')"
                  @click="confirmRemoveSaved(item)"
                />
              </div>
            </div>
          </li>
        </ul>
      </div>

      <p v-if="formError" class="mb-0 text-sm text-n-ruby-9">
        {{ formError }}
      </p>
    </div>
  </Dialog>
</template>
